import { useEffect } from 'react';

/**
 * SEO — sets <title>, meta description, canonical, OpenGraph, Twitter Card,
 * and (optionally) JSON-LD structured data on the current page.
 *
 * On mount we upgrade any pre-existing (base) tags in index.html so we never
 * produce duplicates; on unmount we tidy stale JSON-LD.
 */

const SITE_URL = 'https://evermark.com';
const SITE_NAME = 'Evermark';
const DEFAULT_OG = 'https://customer-assets-cm19k8pv.emergentagent.net/job_a03997ba-1196-47d5-9215-27d1eb255f12/artifacts/vxczuo9a_Color%20logo%20with%20background.webp';

// Find an existing head element by attribute regardless of data-seo mark;
// if found, take it over by tagging with data-seo so we can update it. Else create.
function upsertMeta(attr, name, content) {
  if (content == null) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"][data-seo]`);
  if (!el) el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('data-seo', 'true');
  el.setAttribute('content', content);
}

function removeMeta(attr, name) {
  document.head.querySelectorAll(`meta[${attr}="${name}"]`).forEach((el) => el.remove());
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"][data-seo]`);
  if (!el) el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('data-seo', 'true');
  el.setAttribute('href', href);
}

function upsertJsonLd(id, data) {
  const existing = document.head.querySelector(`script[type="application/ld+json"][data-seo-id="${id}"]`);
  if (!data) {
    if (existing) existing.remove();
    return;
  }
  let el = existing;
  if (!el) {
    el = document.createElement('script');
    el.setAttribute('type', 'application/ld+json');
    el.setAttribute('data-seo-id', id);
    el.setAttribute('data-seo', 'true');
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export default function SEO({
  title,
  description,
  path = typeof window !== 'undefined' ? window.location.pathname : '/',
  image,
  type = 'website',
  keywords,
  jsonLd,
  breadcrumb,
  noindex = false,
}) {
  useEffect(() => {
    // Consistent title order: "Evermark — {title}" (site first).
    const fullTitle = title ? `${SITE_NAME} — ${title}` : `${SITE_NAME} — A Legacy in Every Mark`;
    document.title = fullTitle;

    const url = `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;
    const ogImage = image || DEFAULT_OG;
    const desc = description || 'Considered clothing for every day. Designed for everyday confidence.';

    upsertMeta('name', 'description', desc);
    upsertMeta('name', 'keywords', keywords?.length ? keywords.join(', ') : null);
    upsertMeta('name', 'author', SITE_NAME);
    upsertMeta('name', 'robots', noindex ? 'noindex, follow' : 'index, follow');
    upsertLink('canonical', url);

    // OpenGraph
    upsertMeta('property', 'og:type', type);
    upsertMeta('property', 'og:site_name', SITE_NAME);
    upsertMeta('property', 'og:title', fullTitle);
    upsertMeta('property', 'og:description', desc);
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:image', ogImage);
    upsertMeta('property', 'og:image:alt', title || SITE_NAME);

    // Twitter Card
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:site', '@evermark');
    upsertMeta('name', 'twitter:title', fullTitle);
    upsertMeta('name', 'twitter:description', desc);
    upsertMeta('name', 'twitter:image', ogImage);

    // JSON-LD primary schema (always refresh — clears if missing)
    upsertJsonLd('primary', jsonLd || null);

    // Breadcrumb schema
    if (breadcrumb?.length) {
      upsertJsonLd('breadcrumb', {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumb.map((item, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: item.label,
          item: item.to ? `${SITE_URL}${item.to}` : undefined,
        })),
      });
    } else {
      upsertJsonLd('breadcrumb', null);
    }

    // Organization schema always present for brand consistency
    upsertJsonLd('organization', {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
      logo: DEFAULT_OG,
      sameAs: [
        'https://instagram.com/evermark',
        'https://twitter.com/evermark',
        'https://facebook.com/evermark',
      ],
    });
  }, [title, description, path, image, type, keywords, jsonLd, breadcrumb, noindex]);

  return null;
}

export { SITE_URL, SITE_NAME };
